// Settings › Tutorials (ART-293) — the how-to videos the admin console controls. Two reads, one per shell: a band
// seat reads `/bands/{bandId}/tutorials`, an org seat `/orgs/{orgId}/tutorials`. WHICH audience's list comes back is
// the server's decision (the route the request arrives on), so the client never names one.
import { useQuery } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContextOrNull } from "../bandContext.js";
import { useOrgContextOrNull } from "../orgContext.js";
import { ApiError } from "./apiError.js";

type TutorialViewDto = components["schemas"]["TutorialView"];

/** One tutorial as Settings shows it. The generated shape marks every field optional; the server always sends them. */
export interface TutorialView {
  tutorialId: string;
  title: string;
  position: number;
  youtubeVideoId: string;
  /** ART-311: a Pro-only video. A Standard band's list never carries one; an org seat's own list does. */
  proOnly: boolean;
}

const toView = (t: TutorialViewDto): TutorialView => ({
  tutorialId: t.tutorialId ?? "",
  title: t.title ?? "",
  position: t.position ?? 0,
  youtubeVideoId: t.youtubeVideoId ?? "",
  proOnly: t.proOnly ?? false,
});

/** The band seat's tutorials, in list order. Context-safe: off (and empty) with no band mounted. */
export function useBandTutorials() {
  const { api } = useApp();
  const bandId = useBandContextOrNull()?.bandId;
  return useQuery({
    queryKey: ["band", bandId, "tutorials"],
    enabled: !!bandId,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/tutorials", {
        params: { path: { bandId: bandId! } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data.map(toView);
    },
    staleTime: 5 * 60_000,
  });
}

/** The org seat's tutorials (its org type's audience), in list order. Context-safe: off with no org mounted. */
export function useOrgTutorials() {
  const { api } = useApp();
  const orgId = useOrgContextOrNull()?.orgId;
  return useQuery({
    queryKey: ["org", orgId, "tutorials"],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/orgs/{orgId}/tutorials", {
        params: { path: { orgId: orgId! } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data.map(toView);
    },
    staleTime: 5 * 60_000,
  });
}
